// src/pages/BulkListingGenerator/components/useBulkFormState.js
// Shared custom hook for bulk form state management.
// Takes a mode config object and returns all state + handlers needed by the UI.

import { useState, useEffect, useCallback, useRef, useMemo, useContext } from 'react';
import JSZip from 'jszip';
import { useFormStep } from '../../../context/FormStepContext';
import AuthContext from '../../../context/AuthContext';
import { initialNewFormData, initialExistingInput, THEME_CONFIG, SINGLE_GEN_COST, PRODUCT_COPY_COST } from '../../SingleListingGenerator/constants';
import themeSlideCounts from '../../../assets/theme_slide_counts.json';
import formStyles from '../../SingleListingGenerator/SingleListingGeneratorPage.module.css';
import { triggerLowCreditsModal, hasEnoughCreditsOrPrompt } from '../../../utils/lowCreditsModal';
import useSingleFlight from '../../../hooks/useSingleFlight';
import {
    SKU_COLUMNS,
    IS_PARENT_COLUMNS,
    PARENT_SKU_COLUMNS,
    deriveSkuFromTitle,
    BRAND_COLUMNS,
    CATEGORY_COLUMNS,
    SUB_CATEGORY_COLUMNS,
    PRODUCT_TYPE_COLUMNS,
    TITLE_COLUMNS,
    COLOR_COLUMNS,
    MATERIAL_COLUMNS,
    SIZE_COLUMNS,
    EAN_COLUMNS,
    KEYWORDS_COLUMNS,
    DESCRIPTION_COLUMNS,
    findColumnValue,
    findExcelRow,
    matchSkuFolder,
    getDimensionDataFromRow,
} from './bulkColumnMappings';

/**
 * @typedef {Object} ModeConfig
 * @property {'existing'|'new'} listingMode
 * @property {number} initialFormStep - 2 for existing, 1 for new
 * @property {{ feature1: string[], feature2: string[], feature3: string[], feature4: string[], feature5: string[] }} featureColumns
 * @property {(row: Object) => Object} extraFormFields - returns additional formData fields (e.g. bannedKeywords, application1/2)
 * @property {(row: Object) => Object} extractExistingInput - returns existingInput for a given row
 * @property {(row: Object) => Object} extractExtraSkuFields - returns extra fields per SKU item (e.g. parentChildAttributes)
 * @property {(step: number, state: Object) => boolean} isFormStepValid
 * @property {(skuItem: Object) => Object} buildSkuEntryExtra - returns extra fields for manifest entry
 */

/**
 * Custom hook that encapsulates all shared bulk form logic.
 *
 * @param {ModeConfig} config - Mode-specific configuration (stable reference expected)
 * @param {{ parsedData: Object, zipFile: File }} props
 */
const EMPTY_AUTH = {};

export function useBulkFormState(config, { parsedData, zipFile, draftMeta, projectName: initialProjectName }) {
    const authContextValue = useContext(AuthContext);
    const authCtx = useMemo(
        () => authContextValue ?? EMPTY_AUTH,
        [authContextValue]
    );
    const token = authCtx?.token;

    // ── Context ─────────────────────────────────────────────────────────────
    const {
        setFormStep: setContextFormStep,
        setListingMode: setContextListingMode,
        setShowVariantsTab: setContextShowVariantsTab,
        isThemesPanelOpen,
        setIsThemesPanelOpen,
        isFontsPanelOpen,
        setIsFontsPanelOpen,
        setSelectedTheme: setContextSelectedTheme,
        setHandleThemeSelect: setContextHandleThemeSelect,
        setBulkSkuData,
        setOnBulkSkuClick,
        applyThemeToAll
    } = useFormStep();

    // ── Theme state ─────────────────────────────────────────────────────────
    const [themesBySku, setThemesBySku] = useState({});

    // ── SKU selection state ─────────────────────────────────────────────────
    const [skuList, setSkuList] = useState([]);
    const [selectedSku, setSelectedSku] = useState('');

    // Derive active theme (children inherit parent's theme)
    const selectedTheme = useMemo(() => {
        if (!selectedSku) return null;
        const currentItem = skuList.find(item => item.sku === selectedSku);
        const key = (!currentItem?.isParent && currentItem?.parentSku)
            ? currentItem.parentSku
            : selectedSku;
        return themesBySku[key] || null;
    }, [selectedSku, skuList, themesBySku]);

    // ── Images from ZIP ─────────────────────────────────────────────────────
    const [skuImages, setSkuImages] = useState({});
    const [isLoadingImages, setIsLoadingImages] = useState(false);

    // ── Form state ──────────────────────────────────────────────────────────
    const [formStep, setFormStep] = useState(config.initialFormStep);
    const [showVariantsTab, setShowVariantsTab] = useState(false);
    const [variantsInternalStep, setVariantsInternalStep] = useState(1);

    const [formData, setFormData] = useState({ ...initialNewFormData });
    const [existingInput, setExistingInput] = useState({ ...initialExistingInput });
    const [dimensionData, setDimensionData] = useState(null);

    // Image sequences
    const [bPlusSequence, setBPlusSequence] = useState([]);
    const [aPlusSequence, setAPlusSequence] = useState([]);
    const [zoomAreas, setZoomAreas] = useState([]);

    // ASIN-related (pre-set for bulk)
    const [isAsinDataFetched] = useState(true);
    const [asinProductData, setAsinProductData] = useState(null);
    const [asinBulletsLocked, setAsinBulletsLocked] = useState(false);
    const [asinDescriptionLocked, setAsinDescriptionLocked] = useState(false);

    // Loading / error
    const [isLoading, setIsLoading] = useState(false);
    const [bulkResult, setBulkResult] = useState(null);
    const [bulkError, setBulkError] = useState(null);
    const [showQueuePopup, setShowQueuePopup] = useState(false);
    // Background-removal reminder (mirrors single-gen)
    const [showBgReminder, setShowBgReminder] = useState(false);
    const [openRemoveBgSignal, setOpenRemoveBgSignal] = useState(0);
    const [isAsinLookupLoading] = useState(false);
    const [currentAsinValue, setCurrentAsinValue] = useState('');

    // Validation
    const [invalidFields, setInvalidFields] = useState(new Set());
    const [keyFeaturesError, setKeyFeaturesError] = useState(false);
    const [countError] = useState('');
    const [productIdentityImageError, setProductIdentityImageError] = useState('');

    // Variants
    const [variantsDraftData, setVariantsDraftData] = useState(null);
    const [isVariantsValid, setIsVariantsValid] = useState(true);

    // Templates
    const [selectedTemplatesWithImages] = useState([]);

    // Parent/child sidebar
    const [expandedParentSku, setExpandedParentSku] = useState(null);

    // Floating dropdown
    const [isFloatingDropdownOpen, setIsFloatingDropdownOpen] = useState(false);

    // Per-SKU snapshots
    const [skuFormSnapshots, setSkuFormSnapshots] = useState({});

    // ── Draft state ──────────────────────────────────────────────────────────
    const [currentDraftId, setCurrentDraftId] = useState(null);
    const [isSavingDraft, setIsSavingDraft] = useState(false);
    const [draftSaveStatus, setDraftSaveStatus] = useState(null); // 'saved' | 'error' | null

    // ── Refs ─────────────────────────────────────────────────────────────────
    const formRef = useRef(null);
    const generateButtonRef = useRef(null);
    const bgReminderBypassRef = useRef(new Set()); // SKUs the user already acknowledged for bg-removal
    const bgReminderProceedRef = useRef(null);     // action to run on "Continue anyway": advance-SKU, or null → generate
    const asinLookupFormRef = useRef(null);
    const productIdentityRef = useRef(null);
    const draftRestoredForRef = useRef(null); // tracks which parsedData we've restored
    const currentFormStateRef = useRef({
        selectedSku: '', formData: null, existingInput: null,
        bPlusSequence: [], aPlusSequence: [], dimensionData: null, zoomAreas: []
    });
    // Tracks whether there are unsaved edits, so leave/refresh guards only warn when
    // there is something to lose. Reset to false after a successful draft save.
    // A fresh upload starts dirty (the freshly-loaded data is itself unsaved work);
    // resuming an existing draft (draftMeta.draftId present) starts clean until edited.
    const hasUnsavedChangesRef = useRef(!draftMeta?.draftId);

    // ── Build formData from a row (uses config for mode-specific columns) ───
    const buildFormDataFromRow = useCallback((row, sku) => ({
        ...initialNewFormData,
        brandName: findColumnValue(row, BRAND_COLUMNS),
        category: findColumnValue(row, CATEGORY_COLUMNS),
        subCategory: findColumnValue(row, SUB_CATEGORY_COLUMNS),
        productType: findColumnValue(row, PRODUCT_TYPE_COLUMNS),
        skuId: sku,
        modelNumber: sku,
        color: findColumnValue(row, COLOR_COLUMNS),
        material: findColumnValue(row, MATERIAL_COLUMNS),
        size: findColumnValue(row, SIZE_COLUMNS),
        eanNumber: findColumnValue(row, EAN_COLUMNS),
        keywords: findColumnValue(row, KEYWORDS_COLUMNS),
        briefDescription: findColumnValue(row, DESCRIPTION_COLUMNS),
        feature1: findColumnValue(row, config.featureColumns.feature1),
        feature2: findColumnValue(row, config.featureColumns.feature2),
        feature3: findColumnValue(row, config.featureColumns.feature3),
        feature4: findColumnValue(row, config.featureColumns.feature4),
        feature5: findColumnValue(row, config.featureColumns.feature5),
        projectName: sku,
        ...config.extraFormFields(row),
    }), [config]);

    // ── Extract SKU list from Excel ─────────────────────────────────────────
    useEffect(() => {
        if (parsedData?.allExcelRows && parsedData.allExcelRows.length > 0) {
            const seenDerivedSlugs = new Map();
            const skuData = parsedData.allExcelRows.map((row, rowIndex) => {
                let sku = findColumnValue(row, SKU_COLUMNS);
                const title = findColumnValue(row, TITLE_COLUMNS);
                // Derive SKU from Title when SKU column is empty
                if (!sku || !sku.trim()) {
                    const baseSlug = deriveSkuFromTitle(title, rowIndex);
                    const count = seenDerivedSlugs.get(baseSlug) ?? 0;
                    seenDerivedSlugs.set(baseSlug, count + 1);
                    sku = count === 0 ? baseSlug : `${baseSlug}_${rowIndex + 1}`;
                } else {
                    sku = sku.trim();
                }
                const isParentValue = findColumnValue(row, IS_PARENT_COLUMNS);
                const isParent = ['true', 'TRUE', 'yes', 'Yes', '1', 1, true].includes(isParentValue) ||
                    isParentValue === true;
                const parentSku = findColumnValue(row, PARENT_SKU_COLUMNS);

                return {
                    sku,
                    title: title || sku,
                    isParent,
                    parentSku,
                    rowIndex,
                    ...config.extractExtraSkuFields(row),
                };
            }).filter(item => item.sku && item.sku.trim());

            // Remove duplicates (prefer first occurrence)
            const uniqueSkuMap = new Map();
            skuData.forEach(item => {
                if (!uniqueSkuMap.has(item.sku)) {
                    uniqueSkuMap.set(item.sku, item);
                }
            });
            const uniqueSkus = Array.from(uniqueSkuMap.values());

            setSkuList(uniqueSkus);

            // Restore draft snapshots & themes in the same batch so they
            // are available when the initial selectedSku triggers the form
            // population effect. Track by parsedData reference so re-navigation
            // to a different draft works correctly.
            if (parsedData?.draftSnapshots && draftRestoredForRef.current !== parsedData) {
                draftRestoredForRef.current = parsedData;
                setSkuFormSnapshots(parsedData.draftSnapshots);
                if (parsedData.draftThemesBySku) {
                    setThemesBySku(parsedData.draftThemesBySku);
                }
            }

            if (uniqueSkus.length > 0 && !selectedSku) {
                setSelectedSku(uniqueSkus[0].sku);
            }
        }
    }, [parsedData, selectedSku, config]);

    // ── Set draft ID from loaded draft ──────────────────────────────────
    useEffect(() => {
        if (draftMeta?.draftId && !currentDraftId) {
            setCurrentDraftId(draftMeta.draftId);
        }
    }, [draftMeta, currentDraftId]);

    // ── Extract images from ZIP ─────────────────────────────────────────────
    useEffect(() => {
        // If ZIP is not provided (e.g. existing-mode bulk ASIN fetch),
        // allow passing pre-hydrated images directly via parsedData.
        if (!zipFile) {
            if (parsedData?.prefetchedSkuImages && typeof parsedData.prefetchedSkuImages === 'object') {
                setSkuImages(parsedData.prefetchedSkuImages);
            }
            return;
        }

        const extractImagesFromZip = async () => {
            setIsLoadingImages(true);
            try {
                const zip = new JSZip();
                const zipContent = await zip.loadAsync(zipFile);
                const imagesBySku = {};
                const filePromises = [];

                Object.keys(zipContent.files).forEach((filename) => {
                    const file = zipContent.files[filename];
                    if (file.dir) return;
                    if (filename.startsWith('__MACOSX')) return;
                    if (filename.includes('.DS_Store')) return;
                    if (filename.startsWith('._')) return;

                    const lowerName = filename.toLowerCase();
                    if (!lowerName.endsWith('.png') &&
                        !lowerName.endsWith('.jpg') &&
                        !lowerName.endsWith('.jpeg') &&
                        !lowerName.endsWith('.webp')) {
                        return;
                    }

                    const parts = filename.split('/').filter(p => p && !p.startsWith('.'));
                    if (parts.length < 2) return;

                    let skuFolder = parts.length >= 2 ? parts[parts.length - 2] : parts[0];
                    if (parts.length >= 3) {
                        skuFolder = parts[parts.length - 2];
                    } else if (parts.length === 2) {
                        skuFolder = parts[0];
                    }
                    if (!skuFolder) return;

                    const imageName = parts[parts.length - 1];

                    filePromises.push(
                        file.async('blob').then(blob => {
                            const imageFile = new File([blob], imageName, { type: blob.type || 'image/png' });
                            return new Promise((resolve) => {
                                const reader = new FileReader();
                                reader.onloadend = () => {
                                    if (!imagesBySku[skuFolder]) imagesBySku[skuFolder] = [];
                                    imagesBySku[skuFolder].push({ file: imageFile, previewUrl: reader.result, name: imageName });
                                    resolve();
                                };
                                reader.readAsDataURL(blob);
                            });
                        })
                    );
                });

                await Promise.all(filePromises);
                setSkuImages(imagesBySku);
            } catch (error) {
                console.error('Error extracting images from ZIP:', error);
            } finally {
                setIsLoadingImages(false);
            }
        };

        extractImagesFromZip();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [zipFile, parsedData]);

    // ── Snapshot save / restore ─────────────────────────────────────────────
    const saveCurrentSkuToSnapshot = useCallback((sku, data) => {
        if (!sku) return;
        setSkuFormSnapshots(prev => ({
            ...prev,
            [sku]: {
                formData: data.formData,
                existingInput: data.existingInput,
                bPlusSequence: data.bPlusSequence || [],
                aPlusSequence: data.aPlusSequence || [],
                dimensionData: data.dimensionData ?? null,
                zoomAreas: data.zoomAreas || []
            }
        }));
    }, []);

    // Keep ref in sync
    useEffect(() => {
        currentFormStateRef.current = {
            selectedSku, formData, existingInput, bPlusSequence, aPlusSequence, dimensionData, zoomAreas
        };
    }, [selectedSku, formData, existingInput, bPlusSequence, aPlusSequence, dimensionData, zoomAreas]);

    // Mark the form dirty. Called only from genuine user-edit entry points (wrapped context
    // setters + edit handlers below) — NOT from hydration, which uses the raw useState setters
    // directly, so opening a draft/upload leaves the form clean until the user actually edits.
    const markDirty = useCallback(() => { hasUnsavedChangesRef.current = true; }, []);
    const setBPlusSequenceDirty = useCallback((v) => { markDirty(); setBPlusSequence(v); }, [markDirty]);
    const setAPlusSequenceDirty = useCallback((v) => { markDirty(); setAPlusSequence(v); }, [markDirty]);
    const setZoomAreasDirty = useCallback((v) => { markDirty(); setZoomAreas(v); }, [markDirty]);
    const setDimensionDataDirty = useCallback((v) => { markDirty(); setDimensionData(v); }, [markDirty]);
    const setVariantsDraftDataDirty = useCallback((v) => { markDirty(); setVariantsDraftData(v); }, [markDirty]);

    // ── selectSku: save snapshot then switch ────────────────────────────────
    const selectSku = useCallback((newSku) => {
        const state = currentFormStateRef.current;
        if (state.selectedSku && state.selectedSku !== newSku) {
            saveCurrentSkuToSnapshot(state.selectedSku, {
                formData: state.formData,
                existingInput: state.existingInput,
                bPlusSequence: state.bPlusSequence,
                aPlusSequence: state.aPlusSequence,
                dimensionData: state.dimensionData,
                zoomAreas: state.zoomAreas
            });
        }
        setSelectedSku(newSku);
    }, [saveCurrentSkuToSnapshot]);

    // Whether the selected SKU is the last one in the list (drives Next-vs-Generate button).
    // Empty list is treated as "last" so the Generate button still shows.
    const isLastSku = useMemo(() => {
        if (skuList.length === 0) return true;
        return skuList.findIndex(item => item.sku === selectedSku) === skuList.length - 1;
    }, [skuList, selectedSku]);

    // Whether the current SKU should trigger the background-removal reminder: infographics
    // are being generated, the SKU hasn't been acknowledged, and no image on it has had its
    // background removed yet. Shared by the "Next" (onNextSku) and "Generate" (handleSubmit) paths.
    const currentSkuNeedsBgReminder = () => {
        if (parsedData?.generationType === 'product_copy') return false;
        if (bgReminderBypassRef.current.has(selectedSku)) return false;
        return ![...(bPlusSequence || []), ...(aPlusSequence || [])]
            .some(img => img?.backgroundRemoved);
    };

    // Advance to the next SKU (selectSku saves the current SKU's snapshot on switch).
    // Before advancing, remind (per-SKU) if the current SKU has no background removed.
    const onNextSku = useCallback(() => {
        const idx = skuList.findIndex(item => item.sku === selectedSku);
        const next = skuList[idx + 1];
        if (!next) return;
        const advance = () => {
            selectSku(next.sku);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        };
        if (currentSkuNeedsBgReminder()) {
            bgReminderProceedRef.current = advance; // Continue anyway → move to next SKU
            setShowBgReminder(true);
            return;
        }
        advance();
    }, [skuList, selectedSku, selectSku, parsedData, bPlusSequence, aPlusSequence]);

    // ── Update form when SKU changes ────────────────────────────────────────
    useEffect(() => {
        if (!selectedSku || !parsedData?.allExcelRows) return;

        const item = skuList.find(s => s.sku === selectedSku);
        const row = item?.rowIndex != null
            ? parsedData.allExcelRows[item.rowIndex]
            : findExcelRow(parsedData.allExcelRows, selectedSku);

        // Restore from snapshot if available
        const snapshot = skuFormSnapshots[selectedSku];
        if (snapshot) {
            setFormData(snapshot.formData || { ...initialNewFormData });
            const snapshotExisting = snapshot.existingInput || { ...initialExistingInput };
            setExistingInput(snapshotExisting);
            setBPlusSequence(snapshot.bPlusSequence || []);
            setAPlusSequence(snapshot.aPlusSequence || []);
            setDimensionData(snapshot.dimensionData ?? null);
            setZoomAreas(snapshot.zoomAreas || []);
            const firstImg = snapshot.bPlusSequence?.[0] || snapshot.aPlusSequence?.[0];
            setAsinProductData({
                asin: selectedSku,
                title: snapshotExisting.title || selectedSku,
                firstImage: firstImg?.previewUrl || null,
                categories_flat: row?.['_categories_flat'] || ''
            });
            // Snapshot lock state from the original data, not user edits
            if (config.listingMode === 'existing') {
                const origExisting = config.extractExistingInput(row || {});
                setAsinBulletsLocked(origExisting.bullets?.some(b => b.trim()));
                setAsinDescriptionLocked(!!origExisting.description?.trim());
            }
            return;
        }

        if (!row) return;

        // Build formData
        setFormData(buildFormDataFromRow(row, selectedSku));

        // Build existingInput (mode-specific)
        const existingInputFromRow = config.extractExistingInput(row);
        setExistingInput(existingInputFromRow);

        // Snapshot lock state from original row data
        if (config.listingMode === 'existing') {
            setAsinBulletsLocked(existingInputFromRow.bullets?.some(b => b.trim()));
            setAsinDescriptionLocked(!!existingInputFromRow.description?.trim());
        }

        // Dimensions
        setDimensionData(getDimensionDataFromRow(row));

        // ASIN product card
        const titleForCard = findColumnValue(row, TITLE_COLUMNS);
        setAsinProductData({
            asin: selectedSku,
            title: titleForCard || selectedSku,
            firstImage: null,
            categories_flat: row['_categories_flat'] || ''
        });

        // Images
        const matchedFolder = matchSkuFolder(skuImages, selectedSku);
        if (matchedFolder && skuImages[matchedFolder]) {
            const images = skuImages[matchedFolder];
            setBPlusSequence(images);
            setAPlusSequence(images);
            if (images.length > 0) {
                setAsinProductData(prev => ({ ...prev, firstImage: images[0].previewUrl }));
            }
        } else {
            setBPlusSequence([]);
            setAPlusSequence([]);
        }
        setZoomAreas([]);
    }, [selectedSku, parsedData, skuImages, skuList, skuFormSnapshots, config, buildFormDataFromRow]);

    // ── getDefaultDataForSku (for SKUs never opened) ────────────────────────
    const getDefaultDataForSku = useCallback((sku) => {
        if (!parsedData?.allExcelRows) return null;
        const item = skuList.find(s => s.sku === sku);
        const row = item?.rowIndex != null
            ? parsedData.allExcelRows[item.rowIndex]
            : findExcelRow(parsedData.allExcelRows, sku);
        if (!row) return null;

        const formDataFromRow = buildFormDataFromRow(row, sku);
        const existingInputFromRow = config.extractExistingInput(row);

        const matchedFolder = matchSkuFolder(skuImages, sku);
        const images = matchedFolder && skuImages[matchedFolder] ? skuImages[matchedFolder] : [];
        const dimensionFromRow = getDimensionDataFromRow(row);

        return {
            formData: formDataFromRow,
            existingInput: existingInputFromRow,
            bPlusSequence: images,
            aPlusSequence: images,
            dimensionData: dimensionFromRow,
            zoomAreas: []
        };
    }, [parsedData, skuImages, skuList, config, buildFormDataFromRow]);

    // ── Context sync effects ────────────────────────────────────────────────
    useEffect(() => {
        setContextFormStep(formStep);
        return () => { setContextFormStep(null); };
    }, [formStep, setContextFormStep]);

    useEffect(() => {
        setContextListingMode(config.listingMode);
        return () => { setContextListingMode(null); };
    }, [config.listingMode, setContextListingMode]);

    useEffect(() => {
        setContextShowVariantsTab(showVariantsTab);
        return () => { setContextShowVariantsTab(false); };
    }, [showVariantsTab, setContextShowVariantsTab]);

    // ── Auto-open themes panel on infographics tab ──────────────────────────
    const hasAutoOpenedRef = useRef(false);
    const prevInfographicsTabRef = useRef(false);

    useEffect(() => {
        const infographicsStep = showVariantsTab
            ? 6
            : (config.listingMode === 'new' ? 4 : 5);
        const isCurrentlyInfographicsTab = formStep === infographicsStep;

        if (isCurrentlyInfographicsTab && !prevInfographicsTabRef.current && !hasAutoOpenedRef.current) {
            setIsThemesPanelOpen(true);
            hasAutoOpenedRef.current = true;
        }
        if (!isCurrentlyInfographicsTab && prevInfographicsTabRef.current) {
            hasAutoOpenedRef.current = false;
        }
        prevInfographicsTabRef.current = isCurrentlyInfographicsTab;
    }, [formStep, showVariantsTab, config.listingMode, setIsThemesPanelOpen]);

    // ── Theme handling ──────────────────────────────────────────────────────
    const handleThemeSelect = useCallback((theme) => {
        markDirty();
        if (applyThemeToAll) {
            // Apply to every parent SKU (children inherit from parent)
            setThemesBySku(() => {
                const updated = {};
                skuList.forEach(item => {
                    if (item.isParent || !item.parentSku) {
                        updated[item.sku] = theme;
                    }
                });
                return updated;
            });
            return;
        }
        if (!selectedSku) return;
        const currentItem = skuList.find(item => item.sku === selectedSku);
        const key = (!currentItem?.isParent && currentItem?.parentSku)
            ? currentItem.parentSku
            : selectedSku;
        setThemesBySku(prev => ({ ...prev, [key]: theme }));
    }, [selectedSku, skuList, applyThemeToAll, markDirty]);

    // When applyThemeToAll is toggled on, apply the current SKU's theme to all
    useEffect(() => {
        if (applyThemeToAll && selectedTheme) {
            setThemesBySku(() => {
                const updated = {};
                skuList.forEach(item => {
                    if (item.isParent || !item.parentSku) {
                        updated[item.sku] = selectedTheme;
                    }
                });
                return updated;
            });
        }
    }, [applyThemeToAll]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        setContextSelectedTheme(selectedTheme);
    }, [selectedTheme, setContextSelectedTheme]);

    const handleThemeSelectRef = useRef(handleThemeSelect);
    useEffect(() => {
        handleThemeSelectRef.current = handleThemeSelect;
    }, [handleThemeSelect]);

    useEffect(() => {
        setContextHandleThemeSelect(() => (theme) => handleThemeSelectRef.current(theme));
    }, [setContextHandleThemeSelect]);

    // ── SKU thumbnail ───────────────────────────────────────────────────────
    const getSkuThumbnail = useCallback((sku) => {
        const matchedFolder = matchSkuFolder(skuImages, sku);
        if (matchedFolder && skuImages[matchedFolder]?.[0]) {
            return skuImages[matchedFolder][0].previewUrl;
        }
        return null;
    }, [skuImages]);

    // ── Bulk SKU click in theme panel ───────────────────────────────────────
    const handleBulkSkuClick = useCallback(() => {
        setIsThemesPanelOpen(false);
        setIsFontsPanelOpen(false);
    }, [setIsThemesPanelOpen, setIsFontsPanelOpen]);

    // ── Bulk SKU data context sync ──────────────────────────────────────────
    useEffect(() => {
        if (skuList.length > 0 && selectedSku) {
            const selectedItem = skuList.find(item => item.sku === selectedSku);
            const currentIndex = skuList.findIndex(item => item.sku === selectedSku);
            const thumbnail = getSkuThumbnail(selectedSku);

            setBulkSkuData({
                currentSku: selectedSku,
                currentTitle: selectedItem?.title || selectedSku,
                currentIndex: currentIndex + 1,
                totalCount: skuList.length,
                thumbnail: thumbnail
            });
        } else {
            setBulkSkuData(null);
        }
        return () => { setBulkSkuData(null); };
    }, [skuList, selectedSku, getSkuThumbnail, setBulkSkuData]);

    useEffect(() => {
        setOnBulkSkuClick(() => handleBulkSkuClick);
        return () => { setOnBulkSkuClick(() => () => {}); };
    }, [handleBulkSkuClick, setOnBulkSkuClick]);

    // ── Form handlers ───────────────────────────────────────────────────────
    const handleChange = useCallback((e) => {
        const { name, value } = e.target;
        markDirty();
        setFormData(prev => ({ ...prev, [name]: value }));
        if (invalidFields.has(name)) {
            setInvalidFields(prev => {
                const next = new Set(prev);
                next.delete(name);
                return next;
            });
        }
    }, [invalidFields, markDirty]);

    const handleExistingInputChange = useCallback((e) => {
        const { name, value } = e.target;
        markDirty();
        if (name.startsWith('bullet-')) {
            const index = parseInt(name.split('-')[1], 10);
            setExistingInput(prev => {
                const newBullets = [...prev.bullets];
                newBullets[index] = value;
                return { ...prev, bullets: newBullets };
            });
            // Clear bullet invalid fields when user types
            if (invalidFields.size > 0) {
                setInvalidFields(prev => {
                    const next = new Set(prev);
                    Array.from(prev).filter(f => f.startsWith('bullet-')).forEach(f => next.delete(f));
                    return next;
                });
            }
        } else {
            setExistingInput(prev => ({ ...prev, [name]: value }));
            if (invalidFields.has(name)) {
                setInvalidFields(prev => {
                    const next = new Set(prev);
                    next.delete(name);
                    return next;
                });
            }
        }
    }, [invalidFields, markDirty]);

    // Product Copy: no infographics step; manifest must omit image/theme keys
    const includeInfographics = parsedData?.generationType !== 'product_copy';

    // ── Draft delete ────────────────────────────────────────────────────────
    const deleteBulkDraft = useCallback(async (draftId) => {
        if (!draftId) return;
        try {
            const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
            await fetch(`${API_URL}/bulk-generation-v2/delete-draft`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ draft_id: draftId }),
            });
        } catch (err) {
            console.error('Failed to delete bulk draft:', err);
        }
    }, [token]);

    // Resolve the lifestyle image filename for a SKU (last B+ slot per theme).
    // Mirrors single-gen's slot computation: index = (itemsToRender - 1) % bPlus.length.
    const getLifestyleImageName = useCallback((bPlus, themeObj) => {
        const themeId = themeObj?.id;
        if (!themeId || !bPlus || bPlus.length === 0) return null;
        const itemsToRender = THEME_CONFIG[themeId]?.bplus?.length
            ?? themeSlideCounts[themeId]?.bplus
            ?? 0;
        if (itemsToRender <= 0) return null;
        const img = bPlus[(itemsToRender - 1) % bPlus.length];
        return img?.name || img?.file?.name || null;
    }, []);

    // ── Submit (generate ZIP) ───────────────────────────────────────────────
    const submitGeneration = useCallback(async (e) => {
        if (e) e.preventDefault();

        // Proactive credits guard: prompt before generating when the balance can't cover
        // the batch (per-SKU cost × SKU count; product copy is cheaper than a full listing).
        const perUnitCost = parsedData?.generationType === 'product_copy' ? PRODUCT_COPY_COST : SINGLE_GEN_COST;
        if (!hasEnoughCreditsOrPrompt(authCtx?.user?.credits, perUnitCost * skuList.length)) {
            setBulkError('Insufficient credits. Please purchase more credits to continue.');
            window.scrollTo({ top: 0, behavior: 'smooth' });
            return;
        }

        // Require a theme selection when generating infographics
        if (includeInfographics) {
            // Check that at least one theme is selected (covers applyThemeToAll case)
            const hasAnyTheme = Object.values(themesBySku).some(t => t && t.id);
            if (!hasAnyTheme) {
                setBulkError('Please select a theme from the sidebar to continue');
                setIsThemesPanelOpen(true);
                window.scrollTo({ top: 0, behavior: 'smooth' });
                return;
            }
        }

        // Remind the user if the current SKU has no image with its background removed.
        // Per-SKU: skipped once acknowledged for this SKU (bypass set).
        if (currentSkuNeedsBgReminder()) {
            bgReminderProceedRef.current = null; // Continue anyway → generate
            setShowBgReminder(true);
            return;
        }

        setIsLoading(true);
        setBulkError(null);
        setBulkResult(null);
        // Yield so React can paint the processing overlay before blocking the main thread
        await new Promise(resolve => setTimeout(resolve, 0));

        try {
        // Merge all SKU data — read from ref to avoid stale closure after setTimeout yield
        const currentState = currentFormStateRef.current;
        const allData = { ...skuFormSnapshots };
        allData[currentState.selectedSku] = {
            formData: currentState.formData,
            existingInput: currentState.existingInput,
            bPlusSequence: currentState.bPlusSequence,
            aPlusSequence: currentState.aPlusSequence,
            dimensionData: currentState.dimensionData,
            zoomAreas: currentState.zoomAreas,
        };
        skuList.forEach((item) => {
            if (!allData[item.sku]) {
                const defaultData = getDefaultDataForSku(item.sku);
                if (defaultData) allData[item.sku] = defaultData;
            }
        });

        const buildSkuEntry = (snapshot, theme, skuItem) => {
            if (!snapshot) return null;
            const base = {
                formData: snapshot.formData,
                ...(config.listingMode === 'existing' && { existingInput: snapshot.existingInput }),
                dimension: snapshot.dimensionData ? {
                    length: snapshot.dimensionData.length,
                    breadth: snapshot.dimensionData.breadth,
                    height: snapshot.dimensionData.height,
                    units: snapshot.dimensionData.units,
                    isComplete: !!snapshot.dimensionData.isComplete
                } : null,
                ...config.buildSkuEntryExtra(skuItem),
            };
            if (!includeInfographics) return base;
            const bPlus = snapshot.bPlusSequence || [];
            const aPlus = snapshot.aPlusSequence || [];
            const dimImg = snapshot.dimensionData?.dimensionImage;
            return {
                ...base,
                b_plus_images: bPlus.map(img => img.name || img.file?.name || ''),
                a_plus_images: aPlus.map(img => img.name || img.file?.name || ''),
                dimension_image: dimImg?.file?.name || dimImg?.name || null,
                lifestyle_image: getLifestyleImageName(bPlus, theme),
                zoom_areas: (snapshot.zoomAreas || []).map(({ x, y, w, h, label }) => ({ x, y, w, h, label: label || '' })),
                selected_theme: theme || null,
            };
        };

        const parentSkuItems = skuList.filter(item => item.isParent);
        const childSkuItems = skuList.filter(item => !item.isParent);
        const manifest = {};

        if (parentSkuItems.length > 0) {
            parentSkuItems.forEach((parentItem) => {
                const entry = buildSkuEntry(allData[parentItem.sku], themesBySku[parentItem.sku], parentItem);
                if (entry) {
                    const childrenForParent = childSkuItems.filter(
                        c => c.parentSku && String(c.parentSku).trim().toLowerCase() === String(parentItem.sku).trim().toLowerCase()
                    );
                    if (childrenForParent.length > 0) {
                        entry.children = {};
                        childrenForParent.forEach((childItem) => {
                            const childTheme = themesBySku[childItem.parentSku];
                            const childEntry = buildSkuEntry(allData[childItem.sku], childTheme, childItem);
                            if (childEntry && childItem.sku) entry.children[childItem.sku] = childEntry;
                        });
                    }
                    manifest[parentItem.sku] = entry;
                }
            });
        } else {
            skuList.forEach((item) => {
                const entry = buildSkuEntry(allData[item.sku], themesBySku[item.sku], item);
                if (entry) manifest[item.sku] = entry;
            });
        }

        // Persist current SKU
        saveCurrentSkuToSnapshot(selectedSku, { formData, existingInput, bPlusSequence, aPlusSequence, dimensionData, zoomAreas });

        // Build input ZIP (manifest + images)
        const zip = new JSZip();
        zip.file('manifest.json', JSON.stringify(manifest, null, 2));

        const skusToZip = parentSkuItems.length > 0
            ? [...parentSkuItems.map(p => p.sku), ...childSkuItems.map(c => c.sku)]
            : skuList.map(i => i.sku);

        for (const sku of skusToZip) {
            const snap = allData[sku];
            if (!snap) continue;
            const bPlus = snap.bPlusSequence || [];
            const aPlus = snap.aPlusSequence || [];
            const dimImg = snap.dimensionData?.dimensionImage;

            // Track written filenames per SKU so a later write never
            // overwrites a bg-removed file with an original of the same name.
            const written = new Set();
            for (const img of bPlus) {
                const file = img.file || img;
                const name = img.name || file?.name;
                if (file && name && !written.has(name)) {
                    zip.file(`${sku}/${name}`, file);
                    written.add(name);
                }
            }
            for (const img of aPlus) {
                const file = img.file || img;
                const name = img.name || file?.name;
                if (file && name && !written.has(name)) {
                    zip.file(`${sku}/${name}`, file);
                    written.add(name);
                }
            }
            if (dimImg) {
                const file = dimImg.file || dimImg;
                const name = dimImg.name || file?.name;
                if (file && name && !written.has(name)) {
                    zip.file(`${sku}/${name}`, file);
                    written.add(name);
                }
            }
        }

        const blob = await zip.generateAsync({ type: 'blob' });
        const generatedZipFile = new File([blob], 'bulk-input.zip', { type: 'application/zip' });

        // STEP 1 — Always use the generated ZIP (contains current images
        // including any bg-removed versions).  Original uploaded ZIP is stale.
        let zipFileForApi = generatedZipFile;
        let excelFileForApi = null;

        if (Array.isArray(parsedData?.files)) {
            for (const file of parsedData.files) {
                const name = (file.name || '').toLowerCase();
                if (
                    name.endsWith('.xlsx') ||
                    name.endsWith('.xls') ||
                    name.endsWith('.xlsm') ||
                    name.endsWith('.csv')
                ) {
                    excelFileForApi = file;
                }
            }
        }

        // Call bulk-generation-v2 API
        if (!token) {
            setBulkError('Please log in to generate listings.');
            return;
        }

        try {
            const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

            // STEP 2 — Build FormData with required fields and both files
            const apiFormData = new FormData();
            apiFormData.append('manifest', JSON.stringify(manifest));
            apiFormData.append('listingMode', config?.listingMode || 'new');
            apiFormData.append('isProductCopy', parsedData?.generationType === 'product_copy' ? 'true' : 'false');

            if (zipFileForApi) apiFormData.append('zipFile', zipFileForApi);
            if (excelFileForApi) apiFormData.append('excelFile', excelFileForApi);

            const detectedInputType = excelFileForApi
                ? (excelFileForApi.name.toLowerCase().endsWith('.csv') ? 'csv' : 'xlsx')
                : 'zip';
            apiFormData.append('inputType', detectedInputType);
            apiFormData.append('generationType', 'bulk');
            if (initialProjectName) apiFormData.append('projectName', initialProjectName);
            if (currentDraftId) apiFormData.append('draftId', currentDraftId);
            // Queue task in background (like old bulk); backend returns 202 and we show popup
            apiFormData.append('async', 'true');

            // Bulk can take 5–10+ minutes; use 15 min timeout so the request is not abandoned
            const BULK_TIMEOUT_MS = 15 * 60 * 1000;
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), BULK_TIMEOUT_MS);

            let response;
            try {
                response = await fetch(`${API_URL}/bulk-generation-v2/generate`, {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                    },
                    body: apiFormData,
                    signal: controller.signal,
                });
            } finally {
                clearTimeout(timeoutId);
            }

            const data = await response.json();

            // Task queued (202) – show popup like old bulk: "Your task has been queued"
            if (response.status === 202) {
                if (data.task_id) {
                    setIsThemesPanelOpen(false);
                    setIsFontsPanelOpen(false);
                    setShowQueuePopup(true);
                    setIsLoading(false);
                    return;
                }
            }

            // Duplicate request (409)
            if (response.status === 409) {
                setBulkError(data.message || 'A similar request is already being processed. Please wait a moment.');
                setIsLoading(false);
                return;
            }

            if (!response.ok) {
                if (response.status === 402 || data.error_code === 'NO_CREDITS' ||
                    (data.error || data.message || '').toLowerCase().includes('insufficient credit')) {
                    triggerLowCreditsModal();
                }
                throw new Error(data.error || `Request failed (${response.status})`);
            }

            // Sync response (200) – auto-download if downloadUrl present
            const downloadUrl = data.downloadUrl;
            if (downloadUrl) {
                const link = document.createElement('a');
                link.href = downloadUrl;
                link.download = `bulk_results_${data.task_id || 'download'}.zip`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            }

            // Do not set bulkResult – no UI overlay, only auto-download

            // Update credits if returned (single-gen deducts per listing)
            const newCreditValue = data?.remaining_credits;
            if (typeof newCreditValue === 'number' && authCtx?.updateUserCredit) {
                authCtx.updateUserCredit(newCreditValue);
            }
        } catch (err) {
            if (err.name === 'AbortError') {
                setBulkError('Request timed out. Bulk generation can take several minutes; try again or use fewer products.');
            } else {
                setBulkError(err.message || 'Generation failed. Please try again.');
            }
        }
        } finally {
            setIsLoading(false);
        }
    }, [
        selectedSku, formData, existingInput, bPlusSequence, aPlusSequence, dimensionData, zoomAreas,
        skuFormSnapshots, skuList, themesBySku, getDefaultDataForSku, saveCurrentSkuToSnapshot, config,
        includeInfographics, token, authCtx, parsedData, setIsThemesPanelOpen,
        currentDraftId, deleteBulkDraft, getLifestyleImageName,
    ]);

    // Rage clicks on Generate would otherwise fire one paid batch per click —
    // the loading state only guards once React has re-rendered.
    const handleSubmit = useSingleFlight(submitGeneration);

    // ── Background-removal reminder handlers (mirror single-gen) ─────────────
    const handleBgReminderRemove = useCallback(() => {
        setShowBgReminder(false);
        bgReminderBypassRef.current.add(selectedSku);
        bgReminderProceedRef.current = null;
        setOpenRemoveBgSignal(prev => prev + 1); // opens RemoveBackgroundModal for the current SKU
    }, [selectedSku]);

    const handleBgReminderContinue = useCallback(() => {
        setShowBgReminder(false);
        bgReminderBypassRef.current.add(selectedSku); // don't re-prompt this SKU
        const proceed = bgReminderProceedRef.current;
        bgReminderProceedRef.current = null;
        if (proceed) proceed();   // Next-SKU path → advance
        else handleSubmit();      // Generate path → generate (bypass now set for this SKU)
    }, [selectedSku, handleSubmit]);

    // ── Variants tab handlers (mirror single-gen) ───────────────────────────
    const onEnableVariantsTab = useCallback(() => {
        setShowVariantsTab(true);
        setFormStep(config.listingMode === 'new' ? 4 : 5);
    }, [config.listingMode]);

    const onNoVariants = useCallback(() => {
        setShowVariantsTab(false);
        setVariantsDraftData(null);
        setFormStep(config.listingMode === 'new' ? 3 : 4);
    }, [config.listingMode]);

    const handleProductIdentityImages = useCallback((images) => {
        markDirty();
        setBPlusSequence(images);
        setAPlusSequence(images);
        if (images.length > 0) setProductIdentityImageError('');
    }, [markDirty]);

    const handleAsinDataFetched = useCallback(() => {
        // Not used in bulk mode
    }, []);

    // ── Step validation (delegates to config) ───────────────────────────────
    const getTotalFormSteps = useCallback(() => {
        if (!includeInfographics) {
            // Product Copy: New = 3 steps (Product Identity, Details, Description), Existing = 4 (ASIN, Existing, Details, Description)
            return config.listingMode === 'new' ? 3 : 4;
        }
        // Complete Listing: New = 4 steps (+ Infographics), Existing = 5 (+ Infographics)
        if (showVariantsTab) return 6;
        return config.listingMode === 'new' ? 4 : 5;
    }, [showVariantsTab, config.listingMode, includeInfographics]);

    const isFormStepValid = useCallback((step) => {
        return config.isFormStepValid(step, {
            formData, existingInput, showVariantsTab, isVariantsValid, bPlusSequence, aPlusSequence
        });
    }, [config, formData, existingInput, showVariantsTab, isVariantsValid, bPlusSequence, aPlusSequence]);

    const isFormValid = useCallback(() => {
        if (includeInfographics && (!bPlusSequence || bPlusSequence.length === 0)) {
            return { isValid: false, reason: "Please upload at least one image" };
        }
        return { isValid: true, reason: "" };
    }, [bPlusSequence, includeInfographics]);

    const getFormStepValidation = useCallback((step) => {
        if (!config.getFormStepValidation) {
            return { valid: isFormStepValid(step), invalidFields: new Set() };
        }
        return config.getFormStepValidation(step, {
            formData, existingInput, showVariantsTab, isVariantsValid, bPlusSequence, aPlusSequence
        });
    }, [config, formData, existingInput, showVariantsTab, isVariantsValid, bPlusSequence, aPlusSequence, isFormStepValid]);

    const handleFormStepNext = useCallback(() => {
        const totalSteps = getTotalFormSteps();
        const { valid, invalidFields: stepInvalidFields } = getFormStepValidation(formStep);

        if (!valid) {
            // Set keyFeaturesError for the key features step
            const keyFeaturesStep = config.listingMode === 'new' ? 3 : 4;
            if (formStep === keyFeaturesStep && !formData.feature1?.trim()) {
                setKeyFeaturesError(true);
            }
            setInvalidFields(stepInvalidFields);
            return;
        }
        setInvalidFields(new Set());
        setKeyFeaturesError(false);
        if (formStep < totalSteps) {
            setFormStep(prev => prev + 1);
        }
    }, [formStep, getTotalFormSteps, getFormStepValidation, config.listingMode, formData.feature1]);

    // ── Apply / remove invalid field highlighting on the DOM ─────────────────
    useEffect(() => {
        const root = formRef.current;
        if (!root) return;

        const findContainer = (element) => {
            if (!element) return null;
            let current = element.parentElement;
            while (current) {
                const classList = Array.from(current.classList || []);
                if (classList.some(cls =>
                    cls.includes('inputContainer') || cls.includes('inputWrapper') ||
                    cls.includes('formGroup') || cls.includes('productTypeInputContainer') ||
                    cls.includes('dimensionInputContainer')
                )) {
                    return current;
                }
                current = current.parentElement;
            }
            return null;
        };

        const applyHighlighting = () => {
            if (!formRef.current) return;
            const form = formRef.current;

            if (invalidFields.size === 0) {
                form.querySelectorAll(`.${formStyles.fieldInvalid}`).forEach(el => {
                    el.classList.remove(formStyles.fieldInvalid);
                });
                return;
            }

            invalidFields.forEach(fieldName => {
                if (fieldName.startsWith('bullet-')) {
                    form.querySelectorAll(`input[name="${fieldName}"]`).forEach(input => {
                        const container = findContainer(input);
                        container?.classList.add(formStyles.fieldInvalid);
                    });
                } else if (fieldName === 'productType') {
                    const selectInput = form.querySelector('#productType-default');
                    if (selectInput) {
                        const container = findContainer(selectInput);
                        container?.classList.add(formStyles.fieldInvalid);
                    }
                } else if (fieldName === 'briefDescription') {
                    form.querySelectorAll(`textarea[name="${fieldName}"]`).forEach(textarea => {
                        const container = findContainer(textarea);
                        container?.classList.add(formStyles.fieldInvalid);
                    });
                } else {
                    form.querySelectorAll(`input[name="${fieldName}"]`).forEach(input => {
                        const container = findContainer(input);
                        container?.classList.add(formStyles.fieldInvalid);
                    });
                }
            });
        };

        const rafId = requestAnimationFrame(applyHighlighting);
        return () => {
            cancelAnimationFrame(rafId);
            if (formRef.current) {
                formRef.current.querySelectorAll(`.${formStyles.fieldInvalid}`).forEach(el => {
                    el.classList.remove(formStyles.fieldInvalid);
                });
            }
        };
    }, [invalidFields, selectedSku]);

    // ── Auto-clear invalid fields when user fills them in ──────────────────
    useEffect(() => {
        if (invalidFields.size === 0) return;
        const fieldsToCheck = ['brandName', 'modelNumber', 'skuId', 'productType'];
        const cleared = fieldsToCheck.filter(f => formData[f]?.trim() && invalidFields.has(f));
        if (cleared.length > 0) {
            setInvalidFields(prev => {
                const next = new Set(prev);
                cleared.forEach(f => next.delete(f));
                return next;
            });
        }
    }, [formData.brandName, formData.modelNumber, formData.skuId, formData.productType, invalidFields]);

    // ── Clear invalidFields when switching SKUs ──────────────────────────────
    useEffect(() => {
        setInvalidFields(new Set());
        setKeyFeaturesError(false);
    }, [selectedSku]);

    // ── Context value for SingleListingFormProvider ──────────────────────────
    const singleListingFormValue = useMemo(() => ({
        formRef,
        listingMode: config.listingMode,
        formStep,
        formData,
        existingInput,
        dimensionData,
        productIdentityRef,
        asinLookupFormRef,
        generateButtonRef,
        handleSubmit,
        handleChange,
        handleExistingInputChange,
        handleFormStepNext,
        handleProductIdentityImages,
        handleAsinDataFetched,
        setFormData,
        setFormStep,
        setExistingInput,
        setDimensionData: setDimensionDataDirty,
        isLoading,
        isAsinLookupLoading,
        isAsinDataFetched,
        currentAsinValue,
        countError,
        bPlusSequence,
        aPlusSequence,
        setBPlusSequence: setBPlusSequenceDirty,
        setAPlusSequence: setAPlusSequenceDirty,
        zoomAreas,
        setZoomAreas: setZoomAreasDirty,
        openRemoveBgSignal,
        productIdentityImageError,
        keyFeaturesError,
        asinProductData,
        asinResetKey: 0,
        setShouldAdvanceAfterAsinFetch: () => {},
        setIsAsinLookupLoading: () => {},
        setCurrentAsinValue,
        selectedTemplatesWithImages,
        isFormValid,
        getTotalFormSteps,
        initialExistingInput,
        disableAsinAutoFetch: true,
        showVariantsTab,
        invalidFields,
        variantsDraftData,
        setVariantsDraftData: setVariantsDraftDataDirty,
        onVariantsValidationChange: setIsVariantsValid,
        isVariantsValid,
        variantsInternalStep,
        onVariantsInternalStepChange: setVariantsInternalStep,
        onEnableVariantsTab,
        onNoVariants,
        onRemoveAsinData: null,
        asinBulletsLocked,
        asinDescriptionLocked,
        isProductCopy: parsedData?.generationType === 'product_copy',
        parentCount: skuList.filter(item => item.isParent).length,
        isLastSku,
        onNextSku,
    }), [
        formStep, formData, existingInput, dimensionData,
        handleSubmit, handleChange, handleExistingInputChange, handleFormStepNext,
        handleProductIdentityImages, handleAsinDataFetched,
        isLoading, isAsinLookupLoading, isAsinDataFetched, currentAsinValue, countError,
        bPlusSequence, aPlusSequence, zoomAreas, openRemoveBgSignal, productIdentityImageError, keyFeaturesError,
        asinProductData, selectedTemplatesWithImages, isFormValid, getTotalFormSteps,
        showVariantsTab, invalidFields, variantsDraftData, isVariantsValid,
        variantsInternalStep, onEnableVariantsTab, onNoVariants,
        isLastSku, onNextSku,
        asinBulletsLocked, asinDescriptionLocked, config.listingMode,
        parsedData?.generationType, skuList,
    ]);

    // ── Close bulk results ───────────────────────────────────────────────────
    const handleCloseBulkResult = useCallback(() => {
        setBulkResult(null);
        setBulkError(null);
    }, []);

    // ── Derived: parent / child lists ───────────────────────────────────────
    const parentSkus = useMemo(() => skuList.filter(item => item.isParent), [skuList]);
    const childSkus = useMemo(() => skuList.filter(item => !item.isParent), [skuList]);

    // ── Draft save ──────────────────────────────────────────────────────────
    const saveBulkDraft = useCallback(async (isAuto = false) => {
        if (isSavingDraft || skuList.length === 0) return null;
        setIsSavingDraft(true);
        setDraftSaveStatus(null);

        try {
            // Merge current SKU state into snapshots
            const allSnapshots = { ...skuFormSnapshots };
            if (selectedSku) {
                allSnapshots[selectedSku] = { formData, existingInput, bPlusSequence, aPlusSequence, dimensionData, zoomAreas };
            }
            // Include SKUs the user never opened, so their images (from the uploaded ZIP)
            // are preserved in the draft — mirrors handleSubmit's allData construction.
            skuList.forEach((item) => {
                if (!allSnapshots[item.sku]) {
                    const defaultData = getDefaultDataForSku(item.sku);
                    if (defaultData) allSnapshots[item.sku] = defaultData;
                }
            });

            // Strip File objects from snapshots for JSON serialization
            const serializableSnapshots = {};
            for (const [sku, snap] of Object.entries(allSnapshots)) {
                serializableSnapshots[sku] = {
                    formData: snap.formData,
                    existingInput: snap.existingInput,
                    dimensionData: snap.dimensionData ? {
                        length: snap.dimensionData.length,
                        breadth: snap.dimensionData.breadth,
                        height: snap.dimensionData.height,
                        units: snap.dimensionData.units,
                        isComplete: !!snap.dimensionData.isComplete,
                    } : null,
                    zoomAreas: snap.zoomAreas || [],
                    // Store image names only (files go in ZIP)
                    bPlusImageNames: (snap.bPlusSequence || []).map(img => img.name || img.file?.name || ''),
                    aPlusImageNames: (snap.aPlusSequence || []).map(img => img.name || img.file?.name || ''),
                    dimensionImageName: snap.dimensionData?.dimensionImage?.name || snap.dimensionData?.dimensionImage?.file?.name || null,
                };
            }

            const draftPayload = {
                skuList,
                skuFormSnapshots: serializableSnapshots,
                themesBySku,
                generationType: parsedData?.generationType || 'complete_listing',
                formStep,
                listingMode: config.listingMode,
            };

            // Build images ZIP
            const zip = new JSZip();
            let hasImages = false;
            for (const [sku, snap] of Object.entries(allSnapshots)) {
                const images = [...(snap.bPlusSequence || []), ...(snap.aPlusSequence || [])];
                const dimImg = snap.dimensionData?.dimensionImage;
                if (dimImg?.file) {
                    images.push(dimImg);
                }
                for (const img of images) {
                    const file = img.file || img;
                    const name = img.name || file?.name;
                    if (file && name && file instanceof File) {
                        zip.file(`${sku}/${name}`, file);
                        hasImages = true;
                    }
                }
            }

            const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
            let response;

            if (hasImages) {
                // Multipart: only when images ZIP needs uploading
                const apiFormData = new FormData();
                apiFormData.append('draftData', JSON.stringify(draftPayload));
                if (currentDraftId) apiFormData.append('existingDraftId', currentDraftId);
                const zipBlob = await zip.generateAsync({ type: 'blob' });
                apiFormData.append('imagesZip', new File([zipBlob], 'draft-images.zip', { type: 'application/zip' }));
                response = await fetch(`${API_URL}/bulk-generation-v2/save-draft`, {
                    method: 'POST',
                    headers: { 'Authorization': `Bearer ${token}` },
                    body: apiFormData,
                });
            } else {
                // JSON: faster for metadata-only saves
                response = await fetch(`${API_URL}/bulk-generation-v2/save-draft`, {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        draftData: draftPayload,
                        ...(currentDraftId ? { existingDraftId: currentDraftId } : {}),
                    }),
                });
            }

            const result = await response.json();
            if (response.ok && result.success) {
                if (result.action === 'created') {
                    setCurrentDraftId(result.draft_id);
                }
                hasUnsavedChangesRef.current = false; // draft now reflects current state
                setDraftSaveStatus(isAuto ? 'auto_saved' : 'saved');
                // Auto-clear status after 3 seconds
                setTimeout(() => setDraftSaveStatus(null), 3000);
                return result.draft_id;
            } else {
                setDraftSaveStatus('error');
                return null;
            }
        } catch (err) {
            console.error('Failed to save bulk draft:', err);
            setDraftSaveStatus('error');
            return null;
        } finally {
            setIsSavingDraft(false);
        }
    }, [
        isSavingDraft, skuList, skuFormSnapshots, selectedSku, formData,
        existingInput, bPlusSequence, aPlusSequence, dimensionData, zoomAreas,
        themesBySku, parsedData, formStep, config.listingMode,
        currentDraftId, token, getDefaultDataForSku,
    ]);

    // ── Autosave every 5 minutes ──────────────────────────────────────────
    const saveBulkDraftRef = useRef(saveBulkDraft);
    saveBulkDraftRef.current = saveBulkDraft;

    useEffect(() => {
        if (skuList.length === 0) return;
        const intervalId = setInterval(() => {
            saveBulkDraftRef.current(true);
        }, 5 * 60 * 1000);
        return () => clearInterval(intervalId);
    }, [skuList.length]);

    // ── Return everything the UI needs ──────────────────────────────────────
    return {
        // Draft state
        currentDraftId,
        setCurrentDraftId,
        isSavingDraft,
        draftSaveStatus,
        saveBulkDraft,
        deleteBulkDraft,

        // SKU state
        skuList,
        selectedSku,
        selectSku,
        isLastSku,
        hasUnsavedChangesRef,
        parentSkus,
        childSkus,
        expandedParentSku,
        setExpandedParentSku,
        getSkuThumbnail,
        isLoadingImages,

        // Form state
        listingMode: config.listingMode,
        formStep,
        setFormStep,
        showVariantsTab,
        isAsinDataFetched,
        isFormStepValid,
        getTotalFormSteps,

        // Panel state (from context)
        isThemesPanelOpen,
        isFontsPanelOpen,

        // Floating dropdown
        isFloatingDropdownOpen,
        setIsFloatingDropdownOpen,

        // Form context value
        singleListingFormValue,

        // Background-removal reminder
        showBgReminder,
        setShowBgReminder,
        handleBgReminderRemove,
        handleBgReminderContinue,

        // Bulk result
        bulkResult,
        bulkError,
        handleCloseBulkResult,
        showQueuePopup,
        setShowQueuePopup,
        isLoading,
    };
}
